import { useState, useEffect } from "react";

interface WorkspaceDeficiency {
  id: string;
  control_relevance: string;
  control_id: string[];
  deficiency_type: string;
  description: string | null;
  root_cause: string | null;
}

export function useWorkspaceDeficiencies(workspaceName: string) {
  const [workspaceDeficiencies, setWorkspaceDeficiencies] = useState<
    WorkspaceDeficiency[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const loadWorkspaceDeficiencies = async () => {
      try {
        setLoading(true);
        setError("");

        const sp: any = (window as any).sharePointAPI;
        if (!sp?.downloadDeficiencyJson || !sp?.downloadRcmJson) {
          setWorkspaceDeficiencies([]);
          return;
        }

        const [defRes, rcmRes] = await Promise.all([
          sp.downloadDeficiencyJson(),
          sp.downloadRcmJson(),
        ]);

        if (!defRes?.success || !defRes.data) {
          setWorkspaceDeficiencies([]);
          return;
        }

        const rawDef = defRes.data.libraries_deficiency || {};
        const allDeficiencies: WorkspaceDeficiency[] = Object.entries(rawDef).map(
          ([id, value]: [string, any]) => ({
            id,
            control_relevance: value.control_relevance || "",
            control_id: Array.isArray(value.control_id) ? value.control_id : [],
            deficiency_type: value.deficiency_type || "",
            description: value.description ?? null,
            root_cause: value.root_cause ?? null,
          }),
        );

        if (!rcmRes?.success || !rcmRes.data) {
          setWorkspaceDeficiencies([]);
          return;
        }

        const rcmData = rcmRes.data;
        const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};

        // Derive controls linked to this workspace
        const workspaceControls: Array<{
          controlId: string;
          workspace: string[];
        }> = Object.entries(librariesRcm as Record<string, any>)
          .map(([controlId, entry]) => {
            const rawWorkspace = entry.workspace || [];
            const wsArr = Array.isArray(rawWorkspace)
              ? rawWorkspace
              : rawWorkspace
              ? [rawWorkspace]
              : [];

            return {
              controlId,
              workspace: wsArr,
            };
          })
          .filter((c) => c.workspace.includes(workspaceName));

        const workspaceControlIds = new Set(
          workspaceControls.map((c) => String(c.controlId)),
        );

        const filtered = allDeficiencies.filter((def) =>
          (def.control_id || []).some((cid) =>
            workspaceControlIds.has(String(cid)),
          ),
        );

        setWorkspaceDeficiencies(filtered);
      } catch (e: any) {
        setError(
          e?.message || "Unexpected error while loading workspace deficiencies",
        );
        setWorkspaceDeficiencies([]);
      } finally {
        setLoading(false);
      }
    };

    if (workspaceName) {
      loadWorkspaceDeficiencies();
    }
  }, [workspaceName]);

  return {
    deficiencies: workspaceDeficiencies,
    loading,
    error,
    hasDeficiencies: workspaceDeficiencies.length > 0,
  };
}

